import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ReactComponent as Logo } from "@/assets/musio-logos/logo_center_top.svg";
import useMusioApi from "@/contexts/client/musio-client-context";
import { useLocation, useSearchParams } from "wouter";
import { SEARCH_PARAM_TOKEN_KEY } from "@/features/recovery";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { LoadingIndicator } from "@/components/ui/loading-indicator";
import { NotificationRow } from "@/components/ui/notification-row";

const ERROR_TOKEN = "There was an error processing your request";
const ERROR_MESSAGE = "There was an error attempting to cancel your password reset request";
const CONFIRMATION = "You have successfully cancelled your reset password request";

export default function CancelPassword() {
  const { musioApi } = useMusioApi();
  const [_, navigate] = useLocation();
  const [params] = useSearchParams();
  const token = params.get(SEARCH_PARAM_TOKEN_KEY);

  if (!token) {
    navigate("~/");
    return;
  }

  const { isError: isValidationError, isSuccess: isValidationSuccess, isPending, } = musioApi.useQuery("get", "/reset", {
    params: {
      query: {
        token
      }
    }
  }, { enabled: !!token, retry: 0 });

  const { mutate, isSuccess: isCancelSuccess, isError: isCancelError, isPending: isCancelPending } = musioApi.useMutation("delete", "/reset");

  useEffect(() => {
    if (isValidationSuccess) mutate({
      body: {
        token
      }
    })
  }, [isValidationSuccess]);

  if (isPending || isCancelPending) {
    return (
      <div className="w-f>ll h-full flex items-center justify-center">
        <LoadingIndicator className="p-10 musio-glass" />
      </div>
    );
  }

  if (isValidationError) {
    return (
      <div className="flex flex-col items-center justify-center font-inter space-y-2 p-2">
        <div className="w-[128px] h-[128px] flex-shrink-0 flex justify-center items-center">
          <Logo />
        </div>
        <Card className="flex items-center justify-center flex-col musio-glass w-full max-w-[478px] h-full">
          <CardHeader className="flex items-center justify-center card-header-padded">
            <div className="flex items-center justify-between text-musio-lilac">
              <CardTitle className="text-[30px]">Reset Password</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="w-full space-y-2 card-content-padded flex flex-col justify-center items-center">
            <NotificationRow intent={"error"}>
              {ERROR_TOKEN}
            </NotificationRow>
          </CardContent>
          <CardFooter className="card-footer-padded w-full flex flex-col justify-center items-center gap-2">
            <Button className="w-full font-bold bg-musio-green text-musio-mid-gray" onClick={() => navigate("~/")}>
              Return
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center font-inter space-y-2 p-2">
      <div className="w-[128px] h-[128px] flex-shrink-0 flex justify-center items-center">
        <Logo />
      </div>
      <Card className="flex items-center justify-center flex-col musio-glass w-full max-w-[478px] h-full">
        <CardHeader className="flex items-center justify-center card-header-padded">
          <div className="flex items-center justify-between text-musio-lilac">
            <CardTitle className="text-[30px]">Cancel Password</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="w-full space-y-2 card-content-padded flex flex-col justify-center items-center">
          {isCancelSuccess || isCancelError ? (
            <NotificationRow intent={isCancelSuccess ? "success" : "error"}>
              {isCancelSuccess ? CONFIRMATION : ERROR_MESSAGE}
            </NotificationRow>
          ) : null}
        </CardContent>
        <CardFooter className="card-footer-padded w-full flex flex-col justify-center items-center gap-2">
          <Button className="w-full font-bold bg-musio-green text-musio-mid-gray" onClick={() => navigate("~/")}>
            Return
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};
